import { useEffect, useRef, useState } from 'react';
import Button from '../components/Button';
import Card from '../components/Card';
import Input from '../components/Input';
import { crmService } from '../services/crmService';
import { useAuth } from '../store/authContext';
import { useToast } from '../store/toastContext';
import type { AgenteData } from '../types';

const emptyAgente: AgenteData = {
  ativo: false,
  schedule: { data: '', hora: '', minuto: '' },
  prompt_unico: '',
  mensagem: '',
  categorias: [],
  email_template_html: '<p>Ola, tudo bem?</p>',
  whatsapp_template:
    'Ola! Vi que sua empresa pode se beneficiar da nossa solucao. Posso te explicar em 2 minutos?'
};
const FIXED_PREFIX = 'NOME PESSOAS OU NOME EMPRESA.';

export default function AgentePage() {
  const { session } = useAuth();
  const [agente, setAgente] = useState<AgenteData>(emptyAgente);
  const [savingStatus, setSavingStatus] = useState(false);
  const { pushToast } = useToast();
  const emailEditorRef = useRef<HTMLDivElement | null>(null);

  const applyEmailFormat = (command: string, value?: string) => {
    emailEditorRef.current?.focus();
    document.execCommand(command, false, value);
    const html = emailEditorRef.current?.innerHTML || '';
    setAgente((prev) => ({ ...prev, email_template_html: html }));
  };

  const load = async () => {
    const a = await crmService.getAgente();
    setAgente(a);
  };

  useEffect(() => {
    if (!session?.user?.id) return;
    load();
  }, [session?.user?.id]);

  useEffect(() => {
    if (!emailEditorRef.current) return;
    const currentHtml = emailEditorRef.current.innerHTML;
    const nextHtml = agente.email_template_html || '';
    if (currentHtml !== nextHtml) {
      emailEditorRef.current.innerHTML = nextHtml;
    }
  }, [agente.email_template_html]);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Agente</h1>

      <Card className="space-y-3">
        <label className="flex items-center justify-between rounded-xl border border-borderLight px-3 py-3 dark:border-borderDark">
          <span className="text-sm font-semibold">Status do agente</span>
          <button
            type="button"
            disabled={savingStatus}
            onClick={async () => {
              const nextOpen = !agente.ativo;
              setSavingStatus(true);
              try {
                await crmService.setAgenteActive(nextOpen);
                setAgente((prev) => ({ ...prev, ativo: nextOpen }));
                pushToast({ type: 'success', title: nextOpen ? 'Agente ativado.' : 'Agente desativado.' });
              } catch {
                pushToast({ type: 'error', title: 'Nao foi possivel atualizar o status do agente.' });
              } finally {
                setSavingStatus(false);
              }
            }}
            className={`h-8 w-14 rounded-full p-1 transition disabled:cursor-not-allowed disabled:opacity-80 ${agente.ativo ? 'bg-cta' : 'bg-slate-300 dark:bg-slate-700'}`}
          >
            <span className={`block h-6 w-6 rounded-full bg-white transition ${agente.ativo ? 'translate-x-6' : ''}`} />
          </button>
        </label>
        <p className="text-xs text-slate-500 dark:text-slate-300">Esse status usa o campo open do usuario logado.</p>

        <div className="space-y-3">
          <p className="text-sm font-semibold">Horario de funcionamento</p>
          <div className="grid grid-cols-2 gap-3">
            <Input type="time" label="Inicio" value={agente.schedule.hora} onChange={(e) => setAgente((p) => ({ ...p, schedule: { ...p.schedule, hora: e.target.value } }))} />
            <Input
              type="time"
              label="Fim"
              value={agente.schedule.minuto}
              onChange={(e) => setAgente((p) => ({ ...p, schedule: { ...p.schedule, minuto: e.target.value } }))}
            />
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">Template de email</p>
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-borderLight bg-cardLight p-2 dark:border-borderDark dark:bg-cardDark">
            <button type="button" className="rounded-md border border-borderLight px-2 py-1 text-xs font-semibold dark:border-borderDark" onClick={() => applyEmailFormat('bold')}>
              Negrito
            </button>
            <button type="button" className="rounded-md border border-borderLight px-2 py-1 text-xs font-semibold dark:border-borderDark" onClick={() => applyEmailFormat('italic')}>
              Italico
            </button>
            <button type="button" className="rounded-md border border-borderLight px-2 py-1 text-xs font-semibold dark:border-borderDark" onClick={() => applyEmailFormat('underline')}>
              Sublinhado
            </button>
            <select className="h-8 rounded-md border border-borderLight bg-white px-2 text-xs dark:border-borderDark dark:bg-slate-900" onChange={(e) => applyEmailFormat('fontName', e.target.value)} defaultValue="">
              <option value="" disabled>
                Fonte
              </option>
              <option value="Arial">Arial</option>
              <option value="Georgia">Georgia</option>
              <option value="Verdana">Verdana</option>
              <option value="Times New Roman">Times New Roman</option>
            </select>
            <select className="h-8 rounded-md border border-borderLight bg-white px-2 text-xs dark:border-borderDark dark:bg-slate-900" onChange={(e) => applyEmailFormat('fontSize', e.target.value)} defaultValue="">
              <option value="" disabled>
                Tamanho
              </option>
              <option value="2">Pequeno</option>
              <option value="3">Normal</option>
              <option value="4">Grande</option>
              <option value="5">Extra</option>
            </select>
            <label className="inline-flex items-center gap-2 rounded-md border border-borderLight px-2 py-1 text-xs dark:border-borderDark">
              Cor
              <input type="color" onChange={(e) => applyEmailFormat('foreColor', e.target.value)} />
            </label>
          </div>
          <div className="rounded-xl border border-borderLight bg-cardLight p-3 dark:border-borderDark dark:bg-cardDark">
            <p className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">{FIXED_PREFIX}</p>
            <div
              ref={emailEditorRef}
              className="min-h-[150px] text-sm text-textLight outline-none dark:text-textDark"
              contentEditable
              suppressContentEditableWarning
              onInput={(e) => {
                const html = (e.currentTarget as HTMLDivElement).innerHTML;
                setAgente((prev) => ({ ...prev, email_template_html: html }));
              }}
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-300">
            No e-mail, o nome e colocado automaticamente. Quando tiver nome da pessoa, usa ele. Se nao tiver, usa o nome da empresa.
          </p>
        </div>

        <div className="space-y-1.5">
          <label className="block text-sm font-semibold text-slate-600 dark:text-slate-300">Mensagem de WhatsApp</label>
          <div className="rounded-xl border border-borderLight bg-cardLight px-4 py-3 dark:border-borderDark dark:bg-cardDark">
            <p className="mb-2 text-xs font-semibold text-slate-500 dark:text-slate-400">{FIXED_PREFIX}</p>
            <textarea
              value={agente.whatsapp_template || agente.mensagem}
              onChange={(e) =>
                setAgente((prev) => ({
                  ...prev,
                  mensagem: e.target.value,
                  whatsapp_template: e.target.value
                }))
              }
              rows={5}
              className="w-full bg-transparent text-sm text-textLight outline-none transition-all duration-200 dark:text-textDark"
            />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-300">
            No WhatsApp e igual: quando tiver nome da pessoa, usa ele. Se nao tiver, entra o nome da empresa automaticamente.
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            variant="primary"
            disabled={savingStatus}
            onClick={async () => {
              setSavingStatus(true);
              try {
                const updated = await crmService.upsertAgenteWhatts(agente);
                setAgente(updated);
                pushToast({ type: 'success', title: 'Agente salvo com sucesso.' });
              } catch (error) {
                pushToast({ type: 'error', title: error instanceof Error ? error.message : 'Nao foi possivel salvar o agente.' });
              } finally {
                setSavingStatus(false);
              }
            }}
          >
            Salvar agente
          </Button>
        </div>
      </Card>
    </div>
  );
}
